import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { gql, useMutation } from '@apollo/client';
import Split from 'react-split';
import {
  FormProvider,
  UseFormReturn,
  useForm,
  useFormState,
} from 'react-hook-form';
import { DocumentViewerProvider } from '../../contexts/DocumentViewerProvider';
import { AutoDocExtSuggestionProvider } from '../../concepts/AutoDocExtraction/AutoDocExtSuggestionProvider';
import { getReviewId } from '../../query/review';
import { useReviewFeatures } from '../../hooks/reviews/useReviewFeatures';
import * as styles from './Extract.module.css';
import ToolbarNav from './ToolbarNav';
import UnsavedChangesWarning from './UnsavedChangesWarning';
import EditTemplateButton from './EditTemplateButton';
import SaveChangesModal from './SaveChangesModal';
import { captureApolloError } from 'util/apolloError';
import { TemplateModel } from 'concepts/Extraction/TemplateModel';
import { fromModel } from 'concepts/Extraction/ViewModel/ExtractViewModel';
import {
  SaveExtractedDataMutation,
  CompleteExtractedDataMutation,
  SaveFormExtractedDataInput,
} from 'types/graphql';
import { ExtractForm } from 'concepts/Extraction/Extract/ExtractForm';
import { CompleteButton } from 'concepts/Extraction/Extract/CompleteButton';
import { DE2OutstandingSuggestion } from 'concepts/Extraction/Extract/useDE2OutstandingSuggestions';
import { scrollToFieldWhenRendered } from 'concepts/AutoDocExtraction/scrollToField';
import useCancelableAction from 'hooks/useCancelableAction';
import { DE2TrackingContext } from 'concepts/Extraction/Extract/DE2TrackingContext';
import Errors from 'components/Errors';
import { errorMessagesFrom } from 'util/errorMessages';
import { ExtractionTabs } from 'concepts/Extraction/Widgets/ExtractionTabs';
import { ExtractionFormType } from 'concepts/Extraction/ExtractionFormType';
import { BlockRecords } from 'concepts/Extraction/BlocksData';
import { FullTextsPreview } from 'concepts/Extraction/Widgets/FullText/FullTextsPreview';
import { migrate } from 'concepts/Extraction/Blocks/migrate';
import { mutationFailed } from 'util/apolloMutations';
import { Button } from 'components/core';
import { Inline } from 'components/layout';
import { Document } from 'types';
import { QueryProvider } from 'query/QueryProvider';
import {
  tokenizeRoute,
  DATA_EXTRACTION_2_TEMPLATE_CREATE_OR_EDIT_ROUTE,
} from 'query/routes';

export const SAVE_EXTRACTED_DATA_MUTATION = gql`
  mutation SaveExtractedData($input: SaveExtractedDataInput!) {
    saveExtractedData(input: $input) {
      success
      errors {
        message
      }
    }
  }
`;

export const COMPLETE_EXTRACTED_DATA_MUTATION = gql`
  mutation CompleteExtractedData($input: CompleteExtractedDataInput!) {
    completeExtractedData(input: $input) {
      success
      errors {
        message
      }
    }
  }
`;

type ExtractionFormPanelProps = {
  type: ExtractionFormType;
  dataExtractionViewModel: ReturnType<typeof fromModel> | null;
  qualityAssessmentViewModel: ReturnType<typeof fromModel> | null;
  dataExtractionForm: UseFormReturn;
  qualityAssessmentForm: UseFormReturn;
  studyId: number;
  useSuggestions: boolean;
  featuresLoading: boolean;
};

const ExtractionFormPanel = memo(function ExtractionFormPanel({
  type,
  dataExtractionViewModel,
  qualityAssessmentViewModel,
  dataExtractionForm,
  qualityAssessmentForm,
  studyId,
  useSuggestions,
  featuresLoading,
}: ExtractionFormPanelProps) {
  switch (type) {
    case 'data_extraction':
      if (!dataExtractionViewModel) return null;
      return (
        <section className={styles.formPanel} aria-label="Data Extraction">
          <DE2TrackingContext.Provider
            value={{ studyId, useSuggestions, featuresLoading }}
          >
            <FormProvider {...dataExtractionForm}>
              <ExtractForm blocks={dataExtractionViewModel.blocks} />
              <UnsavedChangesWarning />
            </FormProvider>
          </DE2TrackingContext.Provider>
        </section>
      );
    case 'quality_assessment':
      if (!qualityAssessmentViewModel) return null;
      return (
        <section className={styles.formPanel} aria-label="Quality Assessment">
          <FormProvider {...qualityAssessmentForm}>
            <ExtractForm blocks={qualityAssessmentViewModel.blocks} />
            <UnsavedChangesWarning />
          </FormProvider>
        </section>
      );
  }
});

const FormDirtyTracker = ({
  dataExtractionForm,
  qualityAssessmentForm,
  dirtyRef,
}: {
  dataExtractionForm: UseFormReturn;
  qualityAssessmentForm: UseFormReturn;
  dirtyRef: React.MutableRefObject<boolean>;
}): null => {
  const { isDirty: dataExtractionDirty } = useFormState({
    control: dataExtractionForm.control,
  });
  const { isDirty: qualityAssessmentDirty } = useFormState({
    control: qualityAssessmentForm.control,
  });

  useEffect(() => {
    dirtyRef.current = dataExtractionDirty || qualityAssessmentDirty;
  }, [dataExtractionDirty, qualityAssessmentDirty, dirtyRef]);

  return null;
};

export type ExtractProps = {
  dataExtractionModel: TemplateModel | null;
  supersededDataExtractionModel?: TemplateModel;
  dataExtractionFormRevisionId: string | null;
  dataExtractionCurrentRevision: BlockRecords | null;
  qualityAssessmentModel: TemplateModel | null;
  supersededQualityAssessmentModel?: TemplateModel;
  qualityAssessmentFormRevisionId: string | null;
  qualityAssessmentCurrentRevision: BlockRecords | null;
  studyId: string;
  reviewStudyDbId: number;
  fullTexts: Document[];
  title: string;
  backUrl: string;
  singleExtractor: boolean;
  canEditTemplate: boolean;
  nutrientViewerKey: string;
};

const splitSizes: [number, number] = [66, 34];

function Extract({
  dataExtractionModel,
  supersededDataExtractionModel,
  dataExtractionFormRevisionId,
  dataExtractionCurrentRevision,
  qualityAssessmentModel,
  supersededQualityAssessmentModel,
  qualityAssessmentFormRevisionId,
  qualityAssessmentCurrentRevision,
  fullTexts,
  studyId,
  reviewStudyDbId,
  title,
  backUrl,
  singleExtractor,
  canEditTemplate,
  nutrientViewerKey,
}: ExtractProps): JSX.Element {
  const { useSuggestions, isLoading: featuresLoading } = useReviewFeatures();
  const reviewId = getReviewId() ?? 'unknown';
  const [navigateToPreviousPage, setNavigateToPreviousPage] = useState(false);
  const getDataExtractionDefaultValues = () => {
    if (!dataExtractionModel || !dataExtractionCurrentRevision) {
      return;
    }

    if (supersededDataExtractionModel) {
      return migrate(
        dataExtractionModel,
        supersededDataExtractionModel,
        dataExtractionCurrentRevision
      );
    }

    return dataExtractionCurrentRevision;
  };
  const getQualityAssessmentDefaultValues = () => {
    if (!qualityAssessmentModel || !qualityAssessmentCurrentRevision) {
      return;
    }

    if (supersededQualityAssessmentModel) {
      return migrate(
        qualityAssessmentModel,
        supersededQualityAssessmentModel,
        qualityAssessmentCurrentRevision
      );
    }

    return qualityAssessmentCurrentRevision;
  };

  const dataExtractionViewModel = useMemo(
    () => (dataExtractionModel ? fromModel(dataExtractionModel) : null),
    [dataExtractionModel]
  );
  const qualityAssessmentViewModel = useMemo(
    () => (qualityAssessmentModel ? fromModel(qualityAssessmentModel) : null),
    [qualityAssessmentModel]
  );
  const dataExtractionForm = useForm({
    defaultValues: getDataExtractionDefaultValues(),
  });
  const qualityAssessmentForm = useForm({
    defaultValues: getQualityAssessmentDefaultValues(),
  });
  const formDirtyRef = useRef(false);

  const [
    saveDataMutation,
    {
      data: saveData,
      error: saveError,
      loading: saving,
      reset: resetSaveMutation,
    },
  ] = useMutation<SaveExtractedDataMutation>(SAVE_EXTRACTED_DATA_MUTATION);
  if (saveError) captureApolloError(saveError);

  const [
    completeExtractedDataMutation,
    {
      data: completeData,
      error: completeDataError,
      loading: completing,
      reset: resetCompleteMutation,
    },
  ] = useMutation<CompleteExtractedDataMutation>(
    COMPLETE_EXTRACTED_DATA_MUTATION
  );
  if (completeDataError) captureApolloError(completeDataError);

  useEffect(() => {
    if (navigateToPreviousPage) {
      window.location.assign(backUrl);
    }
  }, [navigateToPreviousPage, backUrl]);

  const [selectedTab, updateSelectedTab] = useState(
    'data_extraction' as ExtractionFormType
  );
  const startWaitingForField = useCancelableAction();

  function save(options: { complete: boolean }): Promise<boolean> {
    let dataExtraction: SaveFormExtractedDataInput | null = null;
    let qualityAssessment: SaveFormExtractedDataInput | null = null;

    if (dataExtractionViewModel && dataExtractionFormRevisionId) {
      dataExtraction = {
        formRevisionId: dataExtractionFormRevisionId,
        data: {
          values: dataExtractionForm.getValues(),
        },
      };
    }

    if (qualityAssessmentViewModel && qualityAssessmentFormRevisionId) {
      qualityAssessment = {
        formRevisionId: qualityAssessmentFormRevisionId,
        data: {
          values: qualityAssessmentForm.getValues(),
        },
      };
    }

    const input = {
      studyId: studyId,
      dataExtraction: dataExtraction,
      qualityAssessment: qualityAssessment,
    };

    type Mutation = (options: {
      variables: { input: typeof input };
    }) => Promise<unknown>;

    // Reset the state of the previous mutations to clear error messages
    resetSaveMutation();
    resetCompleteMutation();

    const mutation: Mutation = options.complete
      ? completeExtractedDataMutation
      : saveDataMutation;

    return mutation({ variables: { input } })
      .then((response: any) => {
        if (mutationFailed(response)) {
          return false;
        }
        // Reset form after submission to reset state values, eg: isDirty
        // Allows user to leave the page without getting unsaved changes prompt
        dataExtractionForm.reset({}, { keepValues: true });
        qualityAssessmentForm.reset({}, { keepValues: true });

        if (options.complete) {
          setNavigateToPreviousPage(true);
        }
        return true;
      })
      .catch((e) => {
        MetaError.notify(e);
        return false;
      });
  }

  const [saveChangesModalOpen, setSaveChangesModalOpen] = useState(false);
  const [pendingTemplateNavigation, setPendingTemplateNavigation] = useState(
    false
  );

  const templateUrl = useMemo(
    () =>
      tokenizeRoute(DATA_EXTRACTION_2_TEMPLATE_CREATE_OR_EDIT_ROUTE, {
        review_id: getReviewId(),
        studyId: reviewStudyDbId,
      }).toString(),
    [reviewStudyDbId]
  );

  useEffect(() => {
    if (pendingTemplateNavigation) {
      window.location.assign(templateUrl);
    }
  }, [pendingTemplateNavigation, templateUrl]);

  const handleEditTemplate = () => {
    if (formDirtyRef.current) {
      setSaveChangesModalOpen(true);
    } else {
      window.location.assign(templateUrl);
    }
  };

  const handleDiscardAndEditTemplate = () => {
    dataExtractionForm.reset({}, { keepValues: true });
    qualityAssessmentForm.reset({}, { keepValues: true });
    setPendingTemplateNavigation(true);
  };

  const handleSaveAndEditTemplate = async () => {
    const succeeded = await save({ complete: false });
    if (succeeded) {
      setPendingTemplateNavigation(true);
    } else {
      setSaveChangesModalOpen(false);
    }
  };

  const renderPanel = useCallback(
    (type: ExtractionFormType) => {
      if (type === 'data_extraction' && !dataExtractionViewModel) return null;
      if (type === 'quality_assessment' && !qualityAssessmentViewModel)
        return null;
      return (
        <ExtractionFormPanel
          type={type}
          dataExtractionViewModel={dataExtractionViewModel}
          qualityAssessmentViewModel={qualityAssessmentViewModel}
          dataExtractionForm={dataExtractionForm}
          qualityAssessmentForm={qualityAssessmentForm}
          studyId={reviewStudyDbId}
          useSuggestions={useSuggestions}
          featuresLoading={featuresLoading}
        />
      );
    },
    [
      dataExtractionViewModel,
      qualityAssessmentViewModel,
      dataExtractionForm,
      qualityAssessmentForm,
      studyId,
      useSuggestions,
      featuresLoading,
    ]
  );

  // Suggestions only sit on data extraction fields, so that tab must show.
  const reviewSuggestion = useCallback(
    ({ block }: DE2OutstandingSuggestion) => {
      updateSelectedTab('data_extraction');
      startWaitingForField(() => scrollToFieldWhenRendered(block.id));
    },
    [startWaitingForField]
  );

  const showEditTemplateButton =
    selectedTab === 'data_extraction' &&
    canEditTemplate &&
    !!dataExtractionViewModel;

  return (
    <QueryProvider>
      <DocumentViewerProvider licenseKey={nutrientViewerKey}>
        <AutoDocExtSuggestionProvider
          reviewId={reviewId}
          studyId={reviewStudyDbId}
          useSuggestions={useSuggestions}
        >
          <div className={styles.container}>
            <FormDirtyTracker
              dataExtractionForm={dataExtractionForm}
              qualityAssessmentForm={qualityAssessmentForm}
              dirtyRef={formDirtyRef}
            />
            <header className={styles.header}>
              <ToolbarNav
                backUrl={backUrl}
                title={title}
                right={
                  <Inline spacing={4}>
                    {showEditTemplateButton && (
                      <EditTemplateButton onClick={handleEditTemplate} />
                    )}
                    <Button
                      disabled={saving}
                      onClick={() => {
                        save({
                          complete: false,
                        });
                      }}
                      type="brand"
                      variant="outline"
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                    <CompleteButton
                      blocks={dataExtractionViewModel?.blocks ?? []}
                      form={dataExtractionForm}
                      singleExtractor={singleExtractor}
                      isCompleting={completing}
                      onComplete={() => {
                        save({
                          complete: true,
                        });
                      }}
                      onReviewSuggestions={reviewSuggestion}
                    />
                  </Inline>
                }
              />
              <Errors
                title="Error saving"
                errors={errorMessagesFrom(
                  saveData?.saveExtractedData?.errors,
                  saveError,
                  completeData?.completeExtractedData?.errors,
                  completeDataError
                )}
              />
            </header>
            {saveChangesModalOpen && (
              <SaveChangesModal
                isSaving={saving}
                onSave={handleSaveAndEditTemplate}
                onDiscard={handleDiscardAndEditTemplate}
                onCancel={() => setSaveChangesModalOpen(false)}
              />
            )}
            <Split className={styles.split} sizes={splitSizes} gutterSize={5}>
              <div>
                <FullTextsPreview documents={fullTexts} />
              </div>
              <div className={styles.forms}>
                <ExtractionTabs
                  selected={selectedTab}
                  onSelect={updateSelectedTab}
                  renderPanel={renderPanel}
                />
              </div>
            </Split>
          </div>
        </AutoDocExtSuggestionProvider>
      </DocumentViewerProvider>
    </QueryProvider>
  );
}

export default Extract;
